import { z } from "zod";
import { destinationTypeSchema } from "./destinationType";
import { TARGET_ID_PATTERN } from "../azure/tenantResourceTargets";
import { tenantResourcesViewSchema } from "./tenantResources";
import type { OffboardingList } from "./tenantOffboarding";

/**
 * Tenants (FR-20). The top of the tenancy tree: a tenant owns instances (Development,
 * Production and more); its `keyVaultName` is a pointer to where its secrets live, never a
 * secret (SEC-2). Nothing here is ever deleted: `status` moves to `disabled` instead, because a
 * tenant id is referenced by instances and every audit row ever written about them.
 *
 * Two names, each with one job (round 6, owner 2026-10-01 23:25):
 * - `displayName` (the UI's "Display name", `tenants.display_name`): spaces allowed, editable,
 *   unique; the one name the app shows.
 * - `name` (the UI's "Name", `tenants.name`; rounds 5 and 6 called it the slug and the short
 *   name): immutable after create. It is the stable handle the vault name, the tenant database
 *   name and every operator runbook are derived from, so renaming it would rename things that
 *   already exist elsewhere.
 *
 * Round 6 T-04 (owner 2026-10-02: "Rename. No need to use Customer. Keep it simple"): the
 * customer that sat above each tenant, one to one, folded into it (platform 0027). Its Display
 * name, website, directory and status are the tenant's; `/platform/customers` is gone, with no
 * alias (pre-GA, our client is its only consumer).
 */

export const tenantStatusSchema = z.enum(["active", "disabled"]);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

/** Postgres-, DNS- and Key-Vault-safe: lowercase, no leading or trailing dash. */
export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * A database server host: a DNS name or an IPv4 literal, and nothing that could steer a
 * connection string. `server/db/tenantResolver.ts` refuses anything else before it builds a
 * URL, and `createInstanceBodySchema` refuses it at the edge so the refusal is a 400 the
 * operator reads rather than a provisioning job that throws where nothing records it.
 */
export const HOST_PATTERN = /^[a-zA-Z0-9._-]{1,255}$/;

export const slugSchema = z
  .string()
  .trim()
  .min(1, "Enter a slug.")
  .max(63, "A slug is at most 63 characters.")
  .regex(SLUG_PATTERN, "Use lowercase letters, digits and dashes. Start and end with one.");

/**
 * The longest Name a NEW tenant may have (owner 2026-10-01: "make it 15").
 *
 * Why a cap at all: the tenant's Name goes into the names of its Azure resources so each
 * one shows which tenant it belongs to, and Azure caps some of those names hard. The owner's
 * naming standard (still to come) has shapes such as `kv-eng-{tenant}`, and a Key Vault name is
 * at most 24 characters. When that standard lands, this is the ONE number to revisit: the
 * create schemas and the dialog's auto-fill both read it.
 *
 * Existing tenants keep their names (dashes, longer names): only the create schemas apply it.
 */
export const TENANT_NAME_MAX = 15;

/** A new tenant's Name: lowercase letters and digits only, 1 to `TENANT_NAME_MAX`. */
export const NEW_TENANT_NAME_PATTERN = new RegExp(`^[a-z0-9]{1,${TENANT_NAME_MAX}}$`);

export const NEW_TENANT_NAME_MESSAGE = `Use lowercase letters and digits only, up to ${TENANT_NAME_MAX}.`;

/**
 * The Name of a tenant being created (round 5). Stricter than `slugSchema`, which still
 * describes every tenant made before it and every instance slug.
 */
export const newTenantNameSchema = z
  .string()
  .trim()
  .min(1, NEW_TENANT_NAME_MESSAGE)
  .regex(NEW_TENANT_NAME_PATTERN, NEW_TENANT_NAME_MESSAGE);

/**
 * The Name a Display name suggests: lower case, accents folded to their letter, everything but
 * a-z and 0-9 removed, cut to `TENANT_NAME_MAX`. "Northwind Marketing" becomes
 * `northwindmarket`. Empty when the display name has no letter or digit at all.
 */
export function tenantNameFromDisplayName(displayName: string): string {
  return displayName
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, TENANT_NAME_MAX);
}

/**
 * What a taken Name says, as the admin types and in the create's 409 (owner 2026-10-01 08:50):
 * no `<base>2` suggestion, the admin chooses the name. "Name" is the owner's label (round 6).
 */
export const TENANT_NAME_TAKEN_MESSAGE = "This name is already used. Choose a different one.";

/** What a taken Display name is told, on create and on rename (`tenants_display_name_key`). */
export const TENANT_DISPLAY_NAME_TAKEN_MESSAGE = "A tenant with that display name already exists.";

/** `GET /platform/tenants/name-available?name=`: is this Name free for a new tenant? */
export const tenantNameAvailabilityQuerySchema = z.object({ name: newTenantNameSchema });

export const tenantNameAvailabilitySchema = z.object({ available: z.boolean() });
export type TenantNameAvailability = z.infer<typeof tenantNameAvailabilitySchema>;

export const WEBSITE_MESSAGE =
  "Enter the tenant's website, starting with https://, such as https://www.northwind.com.";

/**
 * The tenant's website (round 5, owner 2026-10-01), kept for reference only: nothing fetches
 * it. https only, and a real host name, so the link the Tenants page renders can only ever
 * open a web page (`z.url` with `protocol`; a `javascript:` or `http:` value is refused). The
 * label stays "Website" (T-04: it is the tenant's website, not a "Tenant URL").
 */
export const websiteSchema = z
  .string()
  .trim()
  .max(500, WEBSITE_MESSAGE)
  // The URL parser forgives "https:/host", so the two slashes and a host are asked for first (#991).
  .regex(/^https:\/\/[^\s/?#]/i, WEBSITE_MESSAGE)
  .pipe(z.url({ protocol: /^https$/, hostname: z.regexes.domain, message: WEBSITE_MESSAGE }));

/**
 * The instances a new tenant starts with (owner 2026-10-01: "A tenant needs at least an
 * instance like Development"). Spelled out, no shortcuts: each is created with this name, the
 * key as its slug, and the name as its environment label.
 */
export const TENANT_START_INSTANCES = [
  { key: "development", name: "Development" },
  { key: "production", name: "Production" },
] as const;
export type TenantStartInstance = (typeof TENANT_START_INSTANCES)[number]["key"];
export const tenantStartInstanceSchema = z.enum(["development", "production"]);

/**
 * What a tenant is for (platform 0043, #824, ADR 0014). `customer` is every tenant a customer uses;
 * `canary` is the one tenant per environment Skypoint runs the daily end-to-end canary in, on a
 * placement target whose `customer` is Skypoint (`CANARY_TARGET_CUSTOMER`). Set once, at create.
 */
export const TENANT_PURPOSES = ["customer", "canary"] as const;
export const tenantPurposeSchema = z.enum(TENANT_PURPOSES);
export type TenantPurpose = z.infer<typeof tenantPurposeSchema>;

/**
 * The placement target `customer` a canary tenant must be created on (ADR 0014): every tenant
 * resource carries its target's `Customer` tag, so a canary on a customer's target would be tagged
 * and billed as that customer's. Compared exactly, as the tag is written.
 */
export const CANARY_TARGET_CUSTOMER = "Skypoint";

/** What a tenant PUT that names `purpose` is told (platform 0043): it is set once, at create. */
export const TENANT_PURPOSE_FIXED =
  "A tenant's purpose is set when it is created and cannot be changed.";

export const tenantViewSchema = z.object({
  id: z.uuid(),
  /** The Display name (`tenants.display_name`, platform 0027): the one name the app shows. */
  displayName: z.string(),
  /** The Name (`tenants.name`, platform 0025): fixed, used in derived names. */
  name: z.string(),
  /** For reference; null for a tenant created before it was asked. */
  website: z.string().nullable(),
  /** The tenant's own Entra directory, for hosted-mode sign-in. Not a credential. */
  entraTenantId: z.uuid().nullable(),
  /** A pointer to the vault, shown read-only; it is not a secret and not editable here. */
  keyVaultName: z.string(),
  status: tenantStatusSchema,
  /** Platform 0043: `customer`, or `canary` for Skypoint's own canary tenant (a badge, PR 3). */
  purpose: tenantPurposeSchema,
  instanceCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** The tenant's own Azure resources (ADR 0010, design §7.2); absent until the web fills it. */
  resources: tenantResourcesViewSchema.optional(),
  /**
   * F32 PR3 (platform 0040): when the tenant was deleted; absent for every tenant that was not.
   * A deleted tenant's `status` stays `disabled`; its offboarding list is
   * `GET /platform/tenants/:id/offboarding`.
   */
  deletedAt: z.string().optional(),
});
export type TenantView = z.infer<typeof tenantViewSchema>;

export const tenantsQuerySchema = z.object({
  search: z.string().max(200).optional(),
  /** Disabled tenants are hidden by default; the admin screen opts in. */
  includeDisabled: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),
  /**
   * F32 PR3: deleted tenants are left out of every list, disabled ones included; `true` lists
   * ONLY the deleted ones (the Tenants page's "Deleted" filter; the route is PlatformAdmin's).
   */
  deleted: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),
});
export type TenantsQuery = z.infer<typeof tenantsQuerySchema>;

/**
 * A new tenant and its first instances, in one transaction (round 5: one dialog; owner
 * 2026-10-01, "a house needs at least a room"). `name` (the dialog's "Name") is set here once and
 * never changes; `keyVaultName` defaults to the deployment's vault. `instances` names which of
 * Development and Production to create, each once, and one `destinationType` (the dialog's
 * "Warehouse") applies to every one of them.
 */
export const createTenantBodySchema = z.object({
  displayName: z.string().trim().min(1).max(200),
  /** Required on create (round 5); optional on update, so a tenant made before it stays editable. */
  website: websiteSchema,
  entraTenantId: z.uuid().nullable().optional(),
  /** A new tenant's Name (round 5): letters and digits, up to `TENANT_NAME_MAX`. */
  name: newTenantNameSchema,
  /**
   * Optional: in dedicated mode every tenant shares the deployment's vault, so the default is
   * the vault this deployment is configured with rather than a name the operator retypes.
   */
  keyVaultName: z.string().trim().min(1).max(127).optional(),
  /**
   * ADR 0010 (design §6.4): the registered placement target for this tenant's own Azure resources,
   * per-tenant mode only (422 otherwise); absent means the platform's default target.
   */
  resourcesTargetId: z.string().regex(TARGET_ID_PATTERN, "Pick a registered target.").optional(),
  instances: z
    .array(tenantStartInstanceSchema)
    .min(1, "Pick at least one instance: Development or Production.")
    .transform((picked) =>
      TENANT_START_INSTANCES.map((entry) => entry.key).filter((key) => picked.includes(key)),
    ),
  destinationType: destinationTypeSchema.default("snowflake"),
  /**
   * Platform 0043 (#824): absent is `customer` (the column's default); `canary` only on a placement
   * target whose `customer` is Skypoint (422 `CANARY_TARGET_NOT_SKYPOINT`). Set once, never changed.
   */
  purpose: tenantPurposeSchema.optional(),
});
export type CreateTenantBody = z.infer<typeof createTenantBodySchema>;
/** What a caller sends (before the defaults and the instance ordering are applied). */
export type CreateTenantInput = z.input<typeof createTenantBodySchema>;

/**
 * What a tenant PUT that names a `name` is told (platform 0025): the Name is set once, at create,
 * because the tenant's database, vault and resource names are derived from it. The Display name
 * is what changes instead.
 */
export const TENANT_NAME_FIXED =
  "A tenant's name cannot change after it is created. Change the display name instead.";

/**
 * Every field optional, at least one required: a PUT that names nothing would write an audit
 * row saying a tenant changed when it did not.
 */
export const updateTenantBodySchema = z
  .object({
    /**
     * Refused (platform 0025): the Name is fixed once created. A 400 naming the field, never a
     * silent strip: a caller that renames here must learn the rename did not happen.
     */
    name: z.never({ error: TENANT_NAME_FIXED }).optional(),
    /** Refused the same way (platform 0043): the purpose is set once, at create. */
    purpose: z.never({ error: TENANT_PURPOSE_FIXED }).optional(),
    displayName: z.string().trim().min(1).max(200).optional(),
    status: tenantStatusSchema.optional(),
    /** Absent leaves it, null clears it, a value must be an https URL. */
    website: websiteSchema.nullable().optional(),
    entraTenantId: z.uuid().nullable().optional(),
    keyVaultName: z.string().trim().min(1).max(127).optional(),
    /**
     * ADR 0010 (Infra change e): Defender for Storage on the tenant's own account, per-tenant
     * tenants only (422 otherwise). Null returns it to the target's default. No `.default()`.
     */
    defenderForStorage: z.boolean().nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "Name at least one field to change.",
  });
export type UpdateTenantBody = z.infer<typeof updateTenantBodySchema>;

/**
 * F32 PR3 (owner decision 2026-10-10): `POST /platform/tenants/:id/delete`. The person types the
 * tenant's Name (fixed, `tenants.name`) to confirm, and says why. Only a DISABLED tenant can be
 * deleted; deleting is final in the app.
 */
export const deleteTenantBodySchema = z
  .object({
    confirmName: z.string().trim().min(1, "Type the tenant's name to confirm.").max(63),
    reason: z.string().trim().min(1, "Say why the tenant is being deleted.").max(500),
  })
  .strict();
export type DeleteTenantBody = z.infer<typeof deleteTenantBodySchema>;

/** What the delete answers: the tenant as it now reads, and its offboarding list. */
export interface TenantDeleted {
  tenant: TenantView;
  offboarding: OffboardingList;
}

/** The refusal while a tenant is still active: disable it first (owner intent, F32). */
export const TENANT_DELETE_NEEDS_DISABLE =
  "Disable this tenant first. Only a disabled tenant can be deleted.";
/** The refusal of any change to a deleted tenant. */
export const TENANT_DELETED_FINAL =
  "This tenant was deleted. A deleted tenant cannot be changed or enabled again in skyData.";
/** The refusal when the typed name does not match. */
export const TENANT_DELETE_NAME_MISMATCH = "The name you typed does not match this tenant's name.";
